<script setup lang="ts">
import type { HomeData } from '~/composables/useHomepage'

const { data } = defineProps<{ data: HomeData | undefined }>()

const hoveredLink = ref<string>()

const isLinkValid = useLinkValidator(true)

const links = useArrayFilter(
  () => data?.exploreCard?.links ?? [],
  (link) => isLinkValid(link.url),
)
</script>

<template>
  <UContainer v-if="data" class="pt-l pb-m lg:py-20">
    <div v-if="data.title || data.description" class="flex flex-col items-center gap-m text-center text-white lg:gap-l">
      <h1
        v-if="data.title"
        class="font-headline text-3xl font-medium lg:text-[3.125rem]"
        v-text="data.title"
      />

      <div
        v-if="data.description"
        class="max-w-3xl text-sm lg:text-xl"
        v-html="data.description"
      />
    </div>

    <div class="mt-12 grid grid-cols-1 gap-s [--roundness:76px] lg:mt-16 lg:grid-cols-3 lg:grid-rows-[min-content_auto]">
      <div class="relative row-span-2 flex flex-col items-center justify-center gap-l rounded-tl-(--roundness) bg-seagreen-50 p-l text-center text-white lg:py-xl">
        <SvgDigitalCircle class="absolute -top-8 -left-8 rotate-on-scroll fill-yellow-pop" />

        <SvgAuniversLogoHorizontal class="max-h-16 lg:hidden" />

        <SvgAuniversLogo class="hidden max-h-48 lg:block" />

        <p class="max-w-4/5 text-center text-base font-normal lg:text-xl">
          Aschehougs digitale læringsunivers
        </p>

        <ul v-if="data.productCards?.length" class="flex flex-wrap items-center justify-center gap-s md:gap-x-legacy-m">
          <li v-for="packet in data.productCards" :key="packet.id">
            <UButton
              class="rounded-full bg-yellow-40 px-legacy-m py-xs text-gray-60 uppercase no-underline transition-colors hover:bg-yellow-30"
              :to="{
                name: 'product',
                params: {
                  slug: packet.slug.short,
                },
              }"
            >
              {{ packet.levels?.[0] ?? packet.name }}
            </UButton>
          </li>
        </ul>
      </div>

      <div v-if="data.exploreCard" class="relative flex items-center gap-xl rounded-tr-(--roundness) bg-green-30 p-(--padding) [--padding:var(--spacing-l)] md:pl-legacy-xl md:[--padding:var(--spacing-s)] lg:col-span-2">
        <div class="flex grow flex-col gap-m lg:gap-l">
          <h2
            v-if="data.exploreCard.title"
            class="font-headline text-3xl text-green-pop md:text-5xl"
            v-text="data.exploreCard.title"
          />

          <ul class="grid grid-cols-2 gap-x-5 gap-y-2 lg:gap-y-5 xl:gap-x-18">
            <li v-for="link in links" :key="link.url">
              <NuxtLink
                :to="link.url"
                class="group flex w-full justify-between gap-xs py-xs font-semibold text-white no-underline"
                @mouseenter="hoveredLink = link.url"
                @focusin="hoveredLink = link.url"
                @mouseleave="hoveredLink = undefined"
                @focusout="hoveredLink = undefined"
              >
                {{ link.label }}

                <UIcon name="far:arrow-right-long" class="hidden shrink-0 transition-transform duration-400 group-hover:translate-x-2 sm:block" />
              </NuxtLink>
            </li>
          </ul>
        </div>

        <div class="relative hidden h-full min-h-80 w-60 overflow-hidden rounded-tr-(--img-roundness) rounded-bl-(--img-roundness) [--img-roundness:calc(var(--roundness)-var(--padding))] md:block">
          <CrystallizeImage
            v-for="link in links"
            :key="link.url"
            :image="link.image"
            class="absolute inset-0 transition-all duration-400"
            img-class="object-cover size-full"
            :class="hoveredLink === link.url ? 'opacity-100 scale-100' : 'opacity-0 scale-105'"
          />
          <CrystallizeImage
            v-if="data.exploreCard?.defaultImage"
            :image="data.exploreCard.defaultImage"
            class="absolute inset-0 transition-all duration-400"
            img-class="object-cover size-full"
            :class="hoveredLink ? 'opacity-0 scale-105' : 'opacity-100 scale-100'"
          />
        </div>
      </div>

      <NuxtLink :to="{ name: 'products' }" class="group flex items-center justify-between gap-s rounded-br-[calc(var(--roundness)/2)] bg-green-10 px-l py-m text-xl font-semibold text-seagreen-60 no-underline transition-colors hover:bg-green-10/80 lg:col-span-2 lg:justify-end">
        Se alle våre produkter
        <UIcon
          name="far:arrow-right-long"
          class="transition-transform duration-400 group-hover:translate-x-2"
        />
      </NuxtLink>
    </div>
  </UContainer>
</template>
